import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ArrowDown, ArrowRight } from "lucide-react";

import { AlertNote } from "../../components/AlertNote";
import { Button } from "../../components/Button";
import { UnifiedModal } from "../../components/UnifiedModal";
import { comparePlans, formatDate, formatPrice, pluralize } from "./billingFormat";

const PlanBlock = ({ name, price }) => {
  const theme = useTheme();
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 700 }}>{name}</Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
        {price ? `${formatPrice(price)} /mes` : "Precio por definir"}
      </Typography>
    </Box>
  );
};

PlanBlock.propTypes = { name: PropTypes.string, price: PropTypes.object };

/**
 * Cambio de plan con suscripción: avisa de los efectos en Markezy y lleva a la confirmación de Stripe,
 * que muestra el importe y cobra. Subir se aplica al pagar; bajar (a otro plan o a Free), al final del periodo.
 */
export const ChangePlanDialog = ({ open, onClose, billing, currentPlan, targetPlan, onConfirm }) => {
  const theme = useTheme();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setSaving(false);
      setError("");
    }
  }, [open]);

  if (!targetPlan) return null;

  const toFree = targetPlan.id === "free";
  // Abierto desde «Cancelar suscripción»: solo se explica la fecha, sin el detalle de los límites de Free
  const cancel = Boolean(targetPlan.cancel);
  const upgrade = comparePlans(billing.plan, targetPlan.id) > 0;
  const periodEnd = formatDate(billing.current_period_end);
  const losesWhiteLabel = Boolean(currentPlan?.white_label) && !targetPlan.white_label;
  const losesClientReporting = Boolean(currentPlan?.client_reporting) && !targetPlan.client_reporting;
  const { active_clients: activeClients, users_count: usersCount, extra_users: extraUsers } = billing;
  const overClients = !toFree && activeClients > targetPlan.max_clients;
  const userLimit = targetPlan.included_users + extraUsers;
  const overUsers = !upgrade && usersCount > userLimit;
  // Stripe anula sin avisar una bajada programada al subir o pasar a Free
  const replacesScheduled = billing.downgrade_at_period_end && billing.scheduled_plan !== targetPlan.id;

  const handleConfirm = async () => {
    setSaving(true);
    setError("");
    try {
      if (!(await onConfirm(targetPlan))) setSaving(false);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "No se ha podido abrir el cambio de plan. Inténtalo de nuevo.");
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  return (
    <UnifiedModal
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      title={cancel ? "Cancelar suscripción" : toFree ? "Pasar al plan Free" : `Cambiar al plan ${targetPlan.name}`}
      disableBackdropClose={saving}
      footer={
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button variant="outline" onClick={handleClose} disabled={saving}>
            {cancel ? "Volver" : "Cancelar"}
          </Button>
          <Button variant={cancel ? "destructive" : "default"} onClick={handleConfirm} disabled={saving}>
            {saving ? "Redirigiendo…" : cancel ? "Cancelar suscripción" : upgrade ? "Ir al pago" : "Continuar"}
          </Button>
        </Stack>
      }
    >
      {cancel ? (
        <Stack spacing={2}>
          <AlertNote variant="info">
            Tu plan {billing.plan_name} seguirá activo hasta el {periodEnd}. Después pasarás al plan Free.
            {billing.seats.has_subscription &&
              ` Tus ${pluralize(extraUsers, "plaza adicional", "plazas adicionales")} se cancelan por separado: en la siguiente pantalla podrás cancelar también ${extraUsers === 1 ? "esa plaza" : "esas plazas"}.`}
          </AlertNote>
          {error && <AlertNote variant="danger">{error}</AlertNote>}
        </Stack>
      ) : (
      <Stack spacing={2}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
          }}
        >
          <PlanBlock name={billing.plan_name} price={currentPlan?.price} />
          <Box sx={{ display: { xs: "none", sm: "flex" }, color: theme.palette.text.secondary }}>
            <ArrowRight size={18} />
          </Box>
          <Box sx={{ display: { xs: "flex", sm: "none" }, color: theme.palette.text.secondary }}>
            <ArrowDown size={18} />
          </Box>
          <PlanBlock name={targetPlan.name} price={targetPlan.price} />
        </Box>

        {upgrade ? (
          <AlertNote variant="info">
            Hoy solo pagas la diferencia por los días que quedan hasta tu próxima renovación. El nuevo plan se activa en
            cuanto se completa el pago.
          </AlertNote>
        ) : (
          <AlertNote variant="info">
            Seguirás con el plan {billing.plan_name} hasta el {periodEnd}. Después pasarás a{" "}
            {toFree ? "Free" : `${targetPlan.name}. Por el cambio no se cobra ni se devuelve nada`}.
            {toFree &&
              extraUsers > 0 &&
              ` Mantendrás tus ${pluralize(extraUsers, "usuario adicional", "usuarios adicionales")}, que se seguirán cobrando cada mes.`}
          </AlertNote>
        )}

        {replacesScheduled && (
          <AlertNote variant="warning">
            Tienes programado el paso a {billing.scheduled_plan_name} el {periodEnd}. Si continúas, ese cambio se anula.
          </AlertNote>
        )}
        {toFree && (
          <AlertNote variant="warning">
            Al pasar a Free tendrás acceso completo a 3 clientes; el resto quedará en solo lectura y te pediremos que
            elijas cuáles mantienes.
          </AlertNote>
        )}
        {losesWhiteLabel && (
          <AlertNote variant="warning">
            Tu logotipo y colores se conservarán, pero dejarán de aplicarse con el plan {targetPlan.name}.
          </AlertNote>
        )}
        {losesClientReporting && (
          <AlertNote variant="warning">
            Con el plan {targetPlan.name} dejarán de generarse los informes mensuales y ni tu equipo ni tus clientes
            podrán verlos. Tus clientes tampoco verán sus métricas.
          </AlertNote>
        )}
        {overClients && (
          <AlertNote variant="warning">
            Tienes {activeClients} clientes activos y {targetPlan.name} permite {targetPlan.max_clients}. No perderás
            ninguno, pero no podrás crear clientes nuevos mientras superes el límite.
          </AlertNote>
        )}
        {overUsers && (
          <AlertNote variant="warning">
            Tienes {usersCount} usuarios y {targetPlan.name} incluye {pluralize(userLimit, "plaza", "plazas")}
            {extraUsers > 0 ? " (con tus plazas adicionales)" : ""}. Cuando se aplique el cambio tendrás que elegir qué
            usuarios mantienes o añadir plazas adicionales, y no podrás invitar a más miembros mientras superes el
            límite.
          </AlertNote>
        )}
        {error && <AlertNote variant="danger">{error}</AlertNote>}
      </Stack>
      )}
    </UnifiedModal>
  );
};

ChangePlanDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  billing: PropTypes.object.isRequired,
  // Plan del catálogo; falta mientras carga la facturación
  currentPlan: PropTypes.object,
  targetPlan: PropTypes.object,
  // useStripeRedirect().go: false si la agencia se queda en la página
  onConfirm: PropTypes.func.isRequired,
};
